const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const scrollBehavior = () => (prefersReducedMotion() ? 'auto' : 'smooth');

export function calculatePageScrollProgress(scrollTop, scrollHeight, viewportHeight) {
  const scrollableDistance = Math.max(0, scrollHeight - viewportHeight);
  if (scrollableDistance === 0) return 0;

  return Math.min(1, Math.max(0, scrollTop / scrollableDistance));
}

export function shouldShowPageScrollTop(scrollProgress, threshold = 0.5) {
  return scrollProgress > threshold;
}

class FilterScrollNavigation extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.updateQueued = false;
    this.handleClick = this.handleClick.bind(this);
    this.queueUpdate = this.queueUpdate.bind(this);

    this.addEventListener('click', this.handleClick);
    this.addEventListener('scroll', this.queueUpdate, true);
    window.addEventListener('scroll', this.queueUpdate, { passive: true });
    window.addEventListener('resize', this.queueUpdate, { passive: true });

    this.resizeObserver = new ResizeObserver(this.queueUpdate);
    this.resizeObserver.observe(this);

    this.mutationObserver = new MutationObserver(this.queueUpdate);
    this.mutationObserver.observe(this, { childList: true, subtree: true });

    requestAnimationFrame(this.queueUpdate);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('scroll', this.queueUpdate, true);
    window.removeEventListener('scroll', this.queueUpdate);
    window.removeEventListener('resize', this.queueUpdate);
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.initialized = false;
  }

  get viewport() {
    return this.querySelector('[data-filter-scroll-viewport]');
  }

  get priceTarget() {
    return this.querySelector('[data-filter-price-target]');
  }

  get button() {
    return this.querySelector('[data-filter-scroll-to-price]');
  }

  queueUpdate() {
    if (this.updateQueued) return;

    this.updateQueued = true;
    requestAnimationFrame(() => {
      this.updateQueued = false;
      this.update();
    });
  }

  update() {
    const viewport = this.viewport;
    const priceTarget = this.priceTarget;
    const button = this.button;
    const stickyBody = this.closest('sticky-sidebar-body');

    if (!viewport || !priceTarget || !button || !stickyBody) {
      viewport?.removeAttribute('data-scroll-active');
      if (button) button.hidden = true;
      return;
    }

    const stickyStyles = getComputedStyle(stickyBody);
    const stickyTop = Number.parseFloat(stickyStyles.top) || 0;
    const stickyActive =
      stickyStyles.position === 'sticky' && stickyBody.getBoundingClientRect().top <= stickyTop + 1;
    const hasOverflow = viewport.scrollHeight > viewport.clientHeight + 1;
    const scrollActive = stickyActive && hasOverflow;
    const priceBelowViewport =
      priceTarget.getBoundingClientRect().top > viewport.getBoundingClientRect().bottom - 16;

    viewport.toggleAttribute('data-scroll-active', scrollActive);

    if (!scrollActive && viewport.scrollTop > 0) {
      viewport.scrollTo({ top: 0, behavior: 'auto' });
    }

    button.hidden = !(scrollActive && priceBelowViewport);
  }

  handleClick(event) {
    const button = event.target.closest('[data-filter-scroll-to-price]');
    if (!button) return;

    const viewport = this.viewport;
    const priceTarget = this.priceTarget;
    if (!viewport || !priceTarget) return;

    const targetTop =
      viewport.scrollTop +
      priceTarget.getBoundingClientRect().top -
      viewport.getBoundingClientRect().top -
      8;

    viewport.scrollTo({
      top: Math.max(0, targetTop),
      behavior: scrollBehavior(),
    });
  }
}

class PageScrollNavigation extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.updateQueued = false;
    this.threshold = Number.parseInt(this.dataset.threshold || '200', 10);
    this.topProgressThreshold = Math.min(
      1,
      Math.max(0, Number.parseFloat(this.dataset.topProgressThreshold || '0.5')),
    );
    this.handleClick = this.handleClick.bind(this);
    this.queueUpdate = this.queueUpdate.bind(this);

    this.addEventListener('click', this.handleClick);
    window.addEventListener('scroll', this.queueUpdate, { passive: true });
    window.addEventListener('resize', this.queueUpdate, { passive: true });

    this.resizeObserver = new ResizeObserver(this.queueUpdate);
    this.resizeObserver.observe(document.body);

    requestAnimationFrame(this.queueUpdate);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    window.removeEventListener('scroll', this.queueUpdate);
    window.removeEventListener('resize', this.queueUpdate);
    this.resizeObserver?.disconnect();
    this.initialized = false;
  }

  queueUpdate() {
    if (this.updateQueued) return;

    this.updateQueued = true;
    requestAnimationFrame(() => {
      this.updateQueued = false;
      this.update();
    });
  }

  update() {
    const topButton = this.querySelector('[data-scroll-to-top]');
    const bottomButton = this.querySelector('[data-scroll-to-bottom]');
    if (!topButton || !bottomButton) return;

    const scrollTop = window.scrollY;
    const scrollHeight = document.documentElement.scrollHeight;
    const remainingScroll = scrollHeight - window.innerHeight - scrollTop;
    const scrollProgress = calculatePageScrollProgress(
      scrollTop,
      scrollHeight,
      window.innerHeight,
    );
    const navigationActive = scrollTop >= this.threshold;
    const showTopButton = shouldShowPageScrollTop(
      scrollProgress,
      this.topProgressThreshold,
    );

    topButton.hidden = !showTopButton;
    bottomButton.hidden = !(navigationActive && remainingScroll > this.threshold);
  }

  handleClick(event) {
    if (event.target.closest('[data-scroll-to-top]')) {
      window.scrollTo({ top: 0, behavior: scrollBehavior() });
      return;
    }

    if (event.target.closest('[data-scroll-to-bottom]')) {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: scrollBehavior(),
      });
    }
  }
}

if (!customElements.get('filter-scroll-navigation')) {
  customElements.define('filter-scroll-navigation', FilterScrollNavigation);
}

if (!customElements.get('page-scroll-navigation')) {
  customElements.define('page-scroll-navigation', PageScrollNavigation);
}
